import { RootState } from '@fiji/common/src/app/store';
import { FirmwareUpgrade, ScheduleDateTime } from '@fiji/common/src/types/ZoneManagement';
import { ScanningSelectedDeviceListModal } from '@fiji/common/src/types/scanningSelectedDeviceListManagement';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type DeviceDetail = {
    deviceTypeId: string;
    parentRealmId: string;
    groupId: string;
    description: string;
    deviceStatus: string;
    organizationId: string;
    deviceConnectionString: string;
    model: string;
    id: string;
    groupLevel: number;
    modelCategory: string;
    serialNumber: string;
    realmId: string;
    deviceTypeName: string;
    parentId: string;
    tags: any[];
    name: string;
    connectionStatus: string;
    subscriptionId: string;
    vendorTypeName: string;
    wrapperDeviceType: string;
    groupName: string;
    groupPath: string[];
    organizationName: string;
    parentDeviceName: string;
    parentDeviceType: string;
    deviceTypeCategory: string;
    deviceModel: string;
    configuredChannels: any[];
};
const initialFirmwareUpgrade: FirmwareUpgrade = {
    isShowFWScheduleModal: false,
    isShowCancelUpgradeModal: false,
    isShowScheduleConfirmationModal: false,
    isShowUpgradeInfoUptoDateModal: false,
    isShowUpgradeInfoScheduledInProgressModal: false,
    isShowUpgradeInfoInterruptedModal: false,
    isFWUpgradeSendToCloud: false,
    failedUpgradeRetryAt: null,
    isLowBattery: false,
    isFirstDeviceOTAFailed: false,
    isShowDeviceLevelFWConfirmationModal: false,
    isShowCompatibilityModal: false,
    isScheduledOtaAlert: false,
    isShowScheduleDateExpiredModal: false,
    scheduleDateTime: {
        date: '',
        time: '',
        timestamp: 0,
        isChange: false,
    },
    localFirmwareFilePath: '',
};

const initialDevice: DeviceDetail = {
    deviceTypeId: '',
    parentRealmId: '',
    groupId: '',
    description: '',
    deviceStatus: '',
    organizationId: '',
    deviceConnectionString: '',
    model: '',
    id: '',
    groupLevel: 0,
    modelCategory: '',
    serialNumber: '',
    realmId: '',
    deviceTypeName: '',
    parentId: '',
    tags: [],
    name: '',
    connectionStatus: '',
    subscriptionId: '',
    vendorTypeName: '',
    wrapperDeviceType: '',
    groupName: '',
    groupPath: [''],
    organizationName: '',
    parentDeviceName: '',
    parentDeviceType: '',
    deviceTypeCategory: '',
    deviceModel: '',
    configuredChannels: [],
};
export interface MeshDetailsDelete {
    isMeshDeleteEnable: boolean;
}
// Type for the provided JSON structure
export interface MeshDeviceDetails {
    applicationKey: string;
    networkKey: string;
    networkId: string;
    connectionStatus: string;
    count: {
        alarmCount: number;
        informationalCount: number;
        offlineCount: number;
        upcomingCount: number;
        warningCount: number;
    };
    dayLightSavingDelta: string;
    dayLightSavingEnd: string;
    dayLightSavingStart: string;
    dayLightSavingVersion: number;
    description: string;
    deviceConnectionString: string;
    deviceCount: number;
    deviceModel: string;
    deviceStatus: string;
    deviceTypeCategory: string;
    deviceTypeId: string;
    deviceTypeName: string;
    firmwareVersion: string;
    groupId: string;
    groupLevel: number;
    groupName: string;
    groupPath: string[];
    id: string;
    isDayLightSavingSynced: boolean;
    isOtaCompleted: boolean;
    isOtaInProgress: boolean;
    isOtaInitiated: boolean;
    isRtcSyncSucceded: boolean;
    isSubscriptionSynced: boolean;
    isSubscriptionUpdated: boolean;
    lastRtcSyncDate: string | null;
    model: string;
    modelCategory: string;
    name: string;
    organizationId: string;
    organizationName: string;
    otaEndDateTime: string | null;
    otaFirstDeviceUnicast: number;
    otaStartDateTime: string | null;
    parentDeviceName: string;
    parentDeviceType: string;
    parentId: string;
    parentRealmId: string;
    profileId: string;
    realmId: string;
    serialNumber: string;
    status: {
        icon: {
            mobile: any;
            web: any;
        };
        indicator: string;
    };
    subscriptionId: string;
    subscriptionVersion: number;
    tags: any[];
    timeToLive: number;
    timeZoneId: string;
    vendorTypeName: string;
    wrapperDeviceType: string;
    zoneCount: number;
    otaStabilityWindow: number;
    otaScheduleTime: number;
    deviceLicenseId: string;
    firmwareMinimumCompatibleVersion: string;
    hasIncompatibleDevices: boolean;
}

export const initialMeshDetailsDelete: MeshDetailsDelete = {
    isMeshDeleteEnable: false,
};

// Initial object for MeshDeviceDetails
export const initialMeshDeviceDetails: MeshDeviceDetails = {
    applicationKey: '',
    networkKey: '',
    networkId: '',
    connectionStatus: '',
    count: {
        alarmCount: 0,
        informationalCount: 0,
        offlineCount: 0,
        upcomingCount: 0,
        warningCount: 0,
    },
    dayLightSavingDelta: '',
    dayLightSavingEnd: '',
    dayLightSavingStart: '',
    dayLightSavingVersion: 0,
    description: '',
    deviceConnectionString: '',
    deviceCount: 0,
    deviceModel: '',
    deviceStatus: '',
    deviceTypeCategory: '',
    deviceTypeId: '',
    deviceTypeName: '',
    firmwareVersion: '',
    groupId: '',
    groupLevel: 0,
    groupName: '',
    groupPath: [],
    id: '',
    isDayLightSavingSynced: false,
    isOtaCompleted: false,
    isOtaInProgress: false,
    isOtaInitiated: false,
    isRtcSyncSucceded: false,
    isSubscriptionSynced: false,
    isSubscriptionUpdated: false,
    lastRtcSyncDate: null,
    model: '',
    modelCategory: '',
    name: '',
    organizationId: '',
    organizationName: '',
    otaEndDateTime: null,
    otaFirstDeviceUnicast: 0,
    otaStartDateTime: null,
    parentDeviceName: '',
    parentDeviceType: '',
    parentId: '',
    parentRealmId: '',
    profileId: '',
    realmId: '',
    serialNumber: '',
    status: {
        icon: {
            mobile: null,
            web: null,
        },
        indicator: '',
    },
    subscriptionId: '',
    subscriptionVersion: 0,
    tags: [],
    timeToLive: 0,
    timeZoneId: '',
    vendorTypeName: '',
    wrapperDeviceType: '',
    zoneCount: 0,
    otaStabilityWindow: 0,
    otaScheduleTime: 0,
    deviceLicenseId: '',
    firmwareMinimumCompatibleVersion: '1.0',
    hasIncompatibleDevices: false,
};

const initialScanningSelectedDeviceListState: ScanningSelectedDeviceListModal[] = [];

// Type guard and creator function
export function createMeshDeviceDetails(json: any): MeshDeviceDetails {
    // Add runtime checks as needed
    return json as MeshDeviceDetails;
}

const meshSlice = createSlice({
    name: 'mesh',
    initialState: {
        meshDevice: initialDevice,
        meshDeviceDetails: initialMeshDeviceDetails,
        firmwareUpgrade: initialFirmwareUpgrade,
        scanningSelectedDeviceList: initialScanningSelectedDeviceListState,
        meshDetailsDelete: initialMeshDetailsDelete,
        shouldRefetchDevice: false,
        shouldRefetchMeshLock: false,
    },
    reducers: {
        setMeshDevice: (state, action: PayloadAction<DeviceDetail>) => {
            state.meshDevice = action.payload;
        },
        setMeshDeviceDetails(state, action: PayloadAction<MeshDeviceDetails>) {
            state.meshDeviceDetails = action.payload;
        },
        clearMeshDevice: (state) => {
            state.meshDevice = initialDevice;
        },
        clearMeshDeviceDetails: (state) => {
            state.meshDeviceDetails = initialMeshDeviceDetails;
        },
        showFWScheduleModal: (state) => {
            state.firmwareUpgrade.isShowFWScheduleModal = true;
        },
        hideFWScheduleModal: (state) => {
            state.firmwareUpgrade.isShowFWScheduleModal = false;
        },
        setFWScheduleDateTime(state, action: PayloadAction<ScheduleDateTime>) {
            state.firmwareUpgrade.scheduleDateTime = action.payload;
        },
        setScanningSelectedDeviceList: (state, action: PayloadAction<ScanningSelectedDeviceListModal[]>) => {
            state.scanningSelectedDeviceList = action.payload;
        },
        showMeshDeviceDetailDelete: (state) => {
            state.meshDetailsDelete.isMeshDeleteEnable = true;
        },
        hideMeshDeiveDetailDelete: (state) => {
            state.meshDetailsDelete.isMeshDeleteEnable = false;
        },
        triggerDeviceRefetch: (state) => {
            // This action will be used to trigger a refetch of device details
            state.shouldRefetchDevice = true;
        },
        clearRefetchFlag: (state) => {
            state.shouldRefetchDevice = false;
        },
        triggerMeshLockRefetch: (state) => {
            state.shouldRefetchMeshLock = true;
        },
        clearMeshLockRefetchFlag: (state) => {
            state.shouldRefetchMeshLock = false;
        },
        setCancelUpgradeModal: (state, action: PayloadAction<boolean>) => {
            state.firmwareUpgrade.isShowCancelUpgradeModal = action.payload;
        },
        setUpgradeConfirmationModal: (state, action: PayloadAction<boolean>) => {
            state.firmwareUpgrade.isShowScheduleConfirmationModal = action.payload;
        },
        setUpgradeInfoModal: (state, action: PayloadAction<boolean>) => {
            state.firmwareUpgrade.isShowScheduleConfirmationModal = action.payload;
        },
        setUpgradeInfoUptoDateModal: (state, action: PayloadAction<boolean>) => {
            state.firmwareUpgrade.isShowUpgradeInfoUptoDateModal = action.payload;
        },
        setUpgradeInfoScheduledInProgressModal: (state, action: PayloadAction<boolean>) => {
            state.firmwareUpgrade.isShowUpgradeInfoScheduledInProgressModal = action.payload;
        },
        setFirmwareFilePath: (state, action: PayloadAction<string>) => {
            state.firmwareUpgrade.localFirmwareFilePath = action.payload;
        },
        clearFirmwareFilePath: (state) => {
            state.firmwareUpgrade.localFirmwareFilePath = '';
        },
        setUpgradeInfoInterruptedModal: (state, action: PayloadAction<boolean>) => {
            state.firmwareUpgrade.isShowUpgradeInfoInterruptedModal = action.payload;
        },
        setFWUpgradeSendToCloud: (state, action: PayloadAction<boolean>) => {
            state.firmwareUpgrade.isFWUpgradeSendToCloud = action.payload;
        },
        setLowBatteryModal: (state, action: PayloadAction<boolean>) => {
            state.firmwareUpgrade.isLowBattery = action.payload;
        },
        setIsCurrentFirstDeviceUpgradeFailed: (state, action: PayloadAction<boolean>) => {
            state.firmwareUpgrade.isFirstDeviceOTAFailed = action.payload;
            if (!action.payload) {
                state.firmwareUpgrade.failedUpgradeRetryAt = null;
            }
        },
        setFailedUpgradeRetryAt: (state, action: PayloadAction<number | null>) => {
            state.firmwareUpgrade.failedUpgradeRetryAt = action.payload;
        },
        setDevicelevelFWConfirmationModal: (state, action: PayloadAction<boolean>) => {
            state.firmwareUpgrade.isShowDeviceLevelFWConfirmationModal = action.payload;
        },
        setCompatibilityModal: (state, action: PayloadAction<boolean>) => {
            state.firmwareUpgrade.isShowCompatibilityModal = action.payload;
        },
        setScheduledOtaAlertModal: (state, action: PayloadAction<boolean>) => {
            state.firmwareUpgrade.isScheduledOtaAlert = action.payload;
        },
        setScheduleDateExpiredModal: (state, action: PayloadAction<boolean>) => {
            state.firmwareUpgrade.isShowScheduleDateExpiredModal = action.payload;
        },
    },
});

// Export actions
export const {
    setMeshDevice,
    setMeshDeviceDetails,
    clearMeshDevice,
    clearMeshDeviceDetails,
    showFWScheduleModal,
    hideFWScheduleModal,
    setFWScheduleDateTime,
    setScanningSelectedDeviceList,
    showMeshDeviceDetailDelete,
    hideMeshDeiveDetailDelete,
    triggerDeviceRefetch,
    clearRefetchFlag,
    triggerMeshLockRefetch,
    clearMeshLockRefetchFlag,
    setCancelUpgradeModal,
    setUpgradeConfirmationModal,
    setUpgradeInfoScheduledInProgressModal,
    setUpgradeInfoUptoDateModal,
    setFirmwareFilePath,
    clearFirmwareFilePath,
    setUpgradeInfoInterruptedModal,
    setFWUpgradeSendToCloud,
    setLowBatteryModal,
    setIsCurrentFirstDeviceUpgradeFailed, // This action can be used to set the state indicating if the current device is the first device being upgraded in a mesh group
    setFailedUpgradeRetryAt,
    setDevicelevelFWConfirmationModal,
    setCompatibilityModal,
    setScheduledOtaAlertModal,
    setScheduleDateExpiredModal,
} = meshSlice.actions;
export const selectMeshDevice = (state: RootState): DeviceDetail => state.mesh.meshDevice;
export const selectMeshDeviceDetails = (state: RootState): MeshDeviceDetails => state.mesh.meshDeviceDetails;
export const showFWScheduleModalStatus = (state: RootState): FirmwareUpgrade => state.mesh.firmwareUpgrade;

export default meshSlice;
